import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronUp,
  Download,
  KeyRound,
  Trash2,
  Users,
  Sparkles,
  Wrench,
} from "lucide-react";

const COLLAPSED_ROWS = 10;

function csvEscape(v) {
  const s = v === null || v === undefined ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildCsv(rows) {
  const header = ["name", "email", "role", "is_editor", "is_bot", "created_at"];
  const lines = [header.join(",")];
  for (const u of rows) {
    lines.push([
      csvEscape(u.name || ""),
      csvEscape(u.email || ""),
      csvEscape(u.role || ""),
      csvEscape(u.is_editor ? "true" : "false"),
      csvEscape(u.is_bot ? "true" : "false"),
      csvEscape(u.created_at || ""),
    ].join(","));
  }
  return lines.join("\n") + "\n";
}

function UserRow({ user, onDeleted, onChanged, readOnly = false, assignablePubs = [] }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  const isAdmin = user.role === "admin";

  const onConfirmDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${user.id}`);
      toast.success(
        `${data.deleted_user} removed${data.predictions_removed ? ` (${data.predictions_removed} predictions cleared)` : ""}`,
      );
      setConfirmOpen(false);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleEditor = async () => {
    setEditorBusy(true);
    try {
      const next = !user.is_editor;
      await api.patch(`/admin/users/${user.id}/editor`, { is_editor: next });
      toast.success(next ? `${user.name || user.email} is now an editor` : `Editor access removed`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditorBusy(false);
    }
  };

  const sendResetLink = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/send-reset`);
      toast.success(`Reset link sent to ${data.sent_to} — expires in ${data.expires_in_minutes} min`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetBusy(false);
    }
  };

  // iter150 — Super-Admin manual re-assignment. Empty value = "Global"
  // (clears pub_id / client_slug / client_id). Any other value is a pub
  // slug. The backend also bumps `token_version` so any live sessions
  // for the target account are invalidated on the next request.
  const onAssignmentChange = async (nextSlug) => {
    setAssignBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/assignment`, {
        pub_slug: nextSlug || null,
      });
      const label = nextSlug
        ? assignablePubs.find((p) => p.slug === nextSlug)?.name || nextSlug
        : "Global";
      toast.success(`${user.name || user.email} → ${label}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAssignBusy(false);
    }
  };

  return (
    <>
      <tr
        className="border-b border-zinc-100 last:border-b-0"
        data-testid={`admin-user-${user.id}`}
      >
        <td className="px-6 py-3 font-medium text-zinc-950">{user.name || "—"}</td>
        <td className="px-6 py-3 text-zinc-700">{user.email}</td>
        <td className="px-6 py-3">
          {isAdmin ? (
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">Global (admin)</span>
          ) : readOnly ? (
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              {user.client_slug ? user.client_slug : "Global"}
            </span>
          ) : assignablePubs.length === 0 ? (
            // iter172 — Defensive: when the /admin/assignable-pubs fetch
            // fails or hasn't returned yet, the <select> below would
            // render "Global" for EVERY user (React collapses an unknown
            // value to the first option). That's the exact "everyone
            // reverted to Global" bug the Super-Admin reported on
            // /editor > Players. Fall back to a read-only chip that
            // faithfully shows the user's actual client_slug so no data
            // is misrepresented while the pubs list is still loading.
            <span
              className="text-[10px] uppercase tracking-[0.15em] text-zinc-500"
              data-testid={`admin-user-assignment-fallback-${user.id}`}
              title="Assignment picker unavailable — refresh to enable edits"
            >
              {user.client_slug ? user.client_slug : "Global"}
            </span>
          ) : (
            <select
              value={user.client_slug || ""}
              onChange={(e) => onAssignmentChange(e.target.value)}
              disabled={assignBusy}
              data-testid={`admin-user-assignment-${user.id}`}
              className="h-9 text-xs rounded-sm border border-zinc-300 bg-white px-2 min-w-[10rem] focus:outline-none focus:ring-1 focus:ring-[#0038FF] disabled:opacity-50"
            >
              <option value="">Global</option>
              {assignablePubs.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.name} ({p.slug})
                </option>
              ))}
              {/* iter172 — Belt-and-braces: if the user's current
                  client_slug isn't in assignablePubs (e.g. their pub was
                  since deleted, or the endpoint returned a shorter list
                  than expected), still render it as a phantom option so
                  the <select> displays the real value instead of
                  silently defaulting to "Global". */}
              {user.client_slug &&
                !assignablePubs.some((p) => p.slug === user.client_slug) && (
                  <option value={user.client_slug}>{user.client_slug} (orphan)</option>
                )}
            </select>
          )}
        </td>
        <td className="px-6 py-3 text-right">
          <div className="flex items-center justify-end gap-1.5 flex-wrap">
            {user.is_bot && (
              <span
                data-testid={`admin-user-bot-badge-${user.id}`}
                className="inline-flex items-center justify-center px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/20"
              >
                Bot
              </span>
            )}
            {user.is_editor && !isAdmin && (
              <span
                data-testid={`admin-user-editor-badge-${user.id}`}
                className="inline-flex items-center justify-center px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200"
              >
                Editor
              </span>
            )}
            <span
              className={`inline-flex items-center justify-center min-w-[3rem] px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest ${
                isAdmin ? "bg-[#0038FF] text-white" : "bg-zinc-100 text-zinc-700"
              }`}
            >
              {user.role}
            </span>
          </div>
        </td>
        <td className="px-6 py-3 text-right whitespace-nowrap">
          {isAdmin ? (
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">Protected</span>
          ) : readOnly ? (
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400" data-testid={`admin-readonly-${user.id}`}>—</span>
          ) : (
            <div className="inline-flex items-center gap-3 justify-end whitespace-nowrap">
              <button
                onClick={toggleEditor}
                disabled={editorBusy}
                data-testid={`admin-toggle-editor-${user.id}`}
                className={`inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] transition-colors ${
                  user.is_editor ? "text-emerald-700 hover:text-emerald-900" : "text-zinc-500 hover:text-zinc-950"
                }`}
              >
                {user.is_editor ? "Remove editor" : "Make editor"}
              </button>
              <button
                onClick={sendResetLink}
                disabled={resetBusy}
                data-testid={`admin-send-reset-${user.id}`}
                title={`Email a password-reset link to ${user.email}`}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-[#0038FF] transition-colors disabled:opacity-50"
              >
                <KeyRound className="h-3.5 w-3.5" /> {resetBusy ? "Sending…" : "Send reset"}
              </button>
              <button
                onClick={() => setConfirmOpen(true)}
                data-testid={`admin-delete-user-${user.id}`}
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-zinc-500 hover:text-[#FF3B30] transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          )}
        </td>
      </tr>

      <Dialog open={confirmOpen} onOpenChange={(v) => !busy && setConfirmOpen(v)}>
        <DialogContent
          className="bg-white rounded-sm border-zinc-200 sm:max-w-md"
          data-testid={`admin-delete-confirm-${user.id}`}
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-[#FF3B30]" />
              Delete user?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              You&apos;re about to permanently delete{" "}
              <span className="font-semibold text-zinc-950">{user.name || user.email}</span>{" "}
              <span className="text-zinc-400">({user.email})</span>. All of their predictions
              and league points will be erased. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              data-testid={`admin-delete-cancel-${user.id}`}
              className="h-11 rounded-sm border-zinc-300"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onConfirmDelete}
              disabled={busy}
              data-testid={`admin-delete-confirm-btn-${user.id}`}
              className="h-11 rounded-sm bg-[#FF3B30] hover:bg-[#E0331F] text-white font-semibold tracking-tight active:scale-[0.98]"
            >
              {busy ? "Deleting…" : "Yes, delete user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PlayersCard({ mode = "admin", scopeSlug = null }) {
  const { user: currentUser } = useAuth();
  const isCurrentUserAdmin = currentUser?.role === "admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  // iter153 — tenancy repair dialog (fix users whose pub_id doesn't match a pub row)
  const [resyncOpen, setResyncOpen] = useState(false);
  const [resyncPreview, setResyncPreview] = useState(null);
  const [resyncBusy, setResyncBusy] = useState(false);
  // iter153 — purge-unpaid-predictions dialog
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeScope, setPurgeScope] = useState("");
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);
  // iter150 — dropdown options for the "Assigned client" column
  const [assignablePubs, setAssignablePubs] = useState([]);

  // In "client-scoped" mode we render a Players list filtered to only
  // users whose `client_slug` matches the current pub's slug (stamped
  // at register-time in `POST /api/auth/register`).
  //
  // iter161 — Destructive actions (delete / editor toggle / send-reset)
  // remain accessible to **Super-Admins** on `/c/<slug>/editor` too —
  // previously they were hidden here because only client-role editors
  // were expected to see this page, but Super-Admins routinely use the
  // slug editor as an impersonation surface and need the same tools
  // they have on `/editor`. Non-admin editors still see a read-only
  // table (the backend endpoints require_admin anyway, so buttons
  // would just 403 for them).
  const isClientScoped = mode === "client-scoped";
  const readOnly = isClientScoped && !isCurrentUserAdmin;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const path = isClientScoped
        ? `/clients/me/players${scopeSlug ? `?scope_slug=${encodeURIComponent(scopeSlug)}` : ""}`
        : `/admin/users`;
      const { data } = await api.get(path);
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [isClientScoped, scopeSlug]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // iter150 — pre-load the pub list once for the assignment dropdown.
  // Skipped in client-scoped mode (readOnly) since we don't render the
  // dropdown there.
  useEffect(() => {
    if (isClientScoped) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/assignable-pubs");
        setAssignablePubs(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [isClientScoped]);

  const botCount = useMemo(
    () => users.filter((u) => u.is_bot).length,
    [users],
  );

  const openCleanup = async () => {
    setCleanupBusy(true);
    setCleanupPreview(null);
    setCleanupOpen(true);
    try {
      const { data } = await api.post(`/admin/users/cleanup-bots`, { dry_run: true });
      setCleanupPreview(data.would_delete);
    } catch (e) {
      toast.error(formatApiError(e));
      setCleanupOpen(false);
    } finally {
      setCleanupBusy(false);
    }
  };

  const confirmCleanup = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post(`/admin/users/cleanup-bots`, { dry_run: false });
      const d = data.deleted || {};
      toast.success(
        `Cleaned up ${d.users || 0} bot/test player(s)` +
          (d.predictions ? ` and ${d.predictions} prediction(s)` : ""),
      );
      setCleanupOpen(false);
      setCleanupPreview(null);
      await loadUsers();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCleanupBusy(false);
    }
  };

  // iter153 — Repair users whose `pub_id` points at a non-existent pub row
  // (typically the parent client_id was stored by mistake). Uses the existing
  // idempotent POST /admin/users/resync-tenancy endpoint.
  const openResync = async () => {
    setResyncBusy(true);
    setResyncPreview(null);
    setResyncOpen(true);
    try {
      const { data } = await api.post(`/admin/users/resync-tenancy`, { dry_run: true });
      setResyncPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setResyncOpen(false);
    } finally {
      setResyncBusy(false);
    }
  };

  const confirmResync = async () => {
    setResyncBusy(true);
    try {
      const { data } = await api.post(`/admin/users/resync-tenancy`, { dry_run: false });
      toast.success(
        `Repaired ${data.applied || 0} player(s)` +
          (data.unrecoverable_count ? ` · ${data.unrecoverable_count} unrecoverable` : ""),
      );
      setResyncOpen(false);
      setResyncPreview(null);
      await loadUsers();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResyncBusy(false);
    }
  };

  // iter153 — Clear predictions submitted by players who did NOT pay the
  // current league entry fee. Scoped to one pub so a super-admin cannot
  // wipe another tenant's data by accident. Uses
  // POST /admin/predictions/purge-unpaid (dry_run then apply).
  const openPurge = () => {
    setPurgePreview(null);
    setPurgeScope("");
    setPurgeOpen(true);
  };

  const runPurgeDryRun = async (slug) => {
    if (!slug) return;
    setPurgeBusy(true);
    setPurgePreview(null);
    try {
      const { data } = await api.post(`/admin/predictions/purge-unpaid`, {
        scope_slug: slug,
        dry_run: true,
      });
      setPurgePreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPurgeBusy(false);
    }
  };

  const confirmPurge = async () => {
    if (!purgeScope) return;
    setPurgeBusy(true);
    try {
      const { data } = await api.post(`/admin/predictions/purge-unpaid`, {
        scope_slug: purgeScope,
        dry_run: false,
      });
      const affected = (data.affected_users || []).length;
      toast.success(
        `Cleared ${data.predictions_deleted || 0} prediction row(s) across ${affected} player(s)`,
      );
      setPurgeOpen(false);
      setPurgePreview(null);
      setPurgeScope("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPurgeBusy(false);
    }
  };

  const visibleUsers = useMemo(
    () => (expanded ? users : users.slice(0, COLLAPSED_ROWS)),
    [users, expanded],
  );

  const exportCsv = () => {
    if (!users.length) {
      toast.error("No players to export.");
      return;
    }
    const csv = buildCsv(users);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `players-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="mt-16" data-testid="admin-players-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
            <Users className="h-5 w-5" /> Players
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            {isClientScoped
              ? `Players who registered through ${scopeSlug ? `/${scopeSlug}` : "this venue"}.${readOnly ? " Read-only." : ""}`
              : "Every registered account, alphabetical by name."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.15em] text-zinc-500" data-testid="admin-users-count">
            {users.length} {users.length === 1 ? "player" : "players"}
          </span>
          <button
            type="button"
            onClick={openResync}
            disabled={loading}
            data-testid="admin-users-resync-tenancy"
            title="Repair players whose pub assignment fields drifted (fixes free-access bugs)"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 hover:text-white hover:bg-amber-600 border border-amber-300 hover:border-amber-600 rounded-sm px-3 h-9 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-amber-800 ${isClientScoped ? "hidden" : ""}`}
          >
            <Wrench className="h-3.5 w-3.5" /> Repair tenancy
          </button>
          <button
            type="button"
            onClick={openPurge}
            disabled={loading}
            data-testid="admin-purge-unpaid-predictions"
            title="Clear predictions from players who submitted without paying the league entry fee"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-white hover:bg-red-600 border border-red-300 hover:border-red-600 rounded-sm px-3 h-9 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-700 ${isClientScoped ? "hidden" : ""}`}
          >
            <Trash2 className="h-3.5 w-3.5" /> Purge unpaid predictions
          </button>
          <button
            type="button"
            onClick={openCleanup}
            disabled={loading || botCount === 0}
            data-testid="admin-users-cleanup-bots"
            title={botCount === 0 ? "No bot / test accounts detected" : `Remove ${botCount} bot / test account(s)`}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-white hover:bg-red-600 border border-red-300 hover:border-red-600 rounded-sm px-3 h-9 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-red-700 ${isClientScoped ? "hidden" : ""}`}
          >
            <Sparkles className="h-3.5 w-3.5" /> Clean up bots{botCount > 0 ? ` (${botCount})` : ""}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={loading || users.length === 0}
            data-testid="admin-users-export"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-9 disabled:opacity-40"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
      ) : users.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-sm p-10 text-center">
          <p className="text-sm text-zinc-500">No players registered yet.</p>
        </div>
      ) : (
        <>
          <div
            className="border border-zinc-200 rounded-sm overflow-x-auto"
            data-testid="admin-users-table"
          >
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Username</th>
                  <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Email</th>
                  <th className="text-left px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Assigned client</th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Role</th>
                  <th className="text-right px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    onDeleted={loadUsers}
                    onChanged={loadUsers}
                    readOnly={readOnly}
                    assignablePubs={assignablePubs}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {users.length > COLLAPSED_ROWS && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid="admin-users-toggle"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-700 hover:text-zinc-950"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Show fewer (first {COLLAPSED_ROWS})
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  Show all {users.length} players
                </>
              )}
            </button>
          )}
        </>
      )}

      <Dialog open={cleanupOpen} onOpenChange={(v) => !cleanupBusy && setCleanupOpen(v)}>
        <DialogContent
          className="bg-white rounded-sm border-zinc-200 sm:max-w-md"
          data-testid="admin-users-cleanup-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-red-600" />
              Clean up bots &amp; test registrations?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              This permanently deletes every user whose email lives at{" "}
              <code className="px-1 bg-zinc-100 rounded-sm text-[11px]">@dugsy.app</code>
              {" "}or whose display name matches <em>test / tester</em>. Admins are always spared. Deletes cascade to their predictions and reset tokens.
            </DialogDescription>
          </DialogHeader>

          {cleanupBusy && !cleanupPreview ? (
            <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 py-4">Checking…</p>
          ) : cleanupPreview ? (
            <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="admin-users-cleanup-preview">
              <p className="font-semibold mb-1">Dry run — this is what will be deleted:</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>
                  <span className="font-semibold">{cleanupPreview.users}</span> user record(s)
                </li>
                <li>
                  <span className="font-semibold">{cleanupPreview.predictions}</span> prediction row(s)
                </li>
                <li>
                  <span className="font-semibold">{cleanupPreview.password_reset_tokens}</span> pending reset token(s)
                </li>
              </ul>
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCleanupOpen(false)}
              disabled={cleanupBusy}
              data-testid="admin-users-cleanup-cancel"
              className="h-11 rounded-sm border-zinc-300"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmCleanup}
              disabled={cleanupBusy || !cleanupPreview || cleanupPreview.users === 0}
              data-testid="admin-users-cleanup-confirm"
              className="h-11 rounded-sm bg-red-600 hover:bg-red-700 text-white font-semibold tracking-tight active:scale-[0.98]"
            >
              {cleanupBusy ? "Cleaning…" : `Yes, delete ${cleanupPreview?.users || 0} account(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resyncOpen} onOpenChange={(v) => !resyncBusy && setResyncOpen(v)}>
        <DialogContent
          className="bg-white rounded-sm border-zinc-200 sm:max-w-md"
          data-testid="admin-users-resync-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
              <Wrench className="h-5 w-5 text-amber-600" />
              Repair player tenancy?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Some players may have a broken <code className="px-1 bg-zinc-100 rounded-sm text-[11px]">pub_id</code>
              {" "}(usually the parent client id got stored by mistake). When that happens their
              pool config falls back to the platform default and they can predict without paying.
              This scan re-derives the correct pub row from each player&apos;s slug and re-stamps
              their tenancy fields. Safe to re-run.
            </DialogDescription>
          </DialogHeader>

          {resyncBusy && !resyncPreview ? (
            <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 py-4">Scanning…</p>
          ) : resyncPreview ? (
            <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="admin-users-resync-preview">
              <p className="font-semibold mb-1">Dry run — what will happen:</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>
                  <span className="font-semibold">{resyncPreview.to_repair_count || 0}</span> player(s) will be repaired
                </li>
                <li>
                  <span className="font-semibold">{resyncPreview.already_ok || 0}</span> already OK
                </li>
                <li>
                  <span className="font-semibold">{resyncPreview.unrecoverable_count || 0}</span> unrecoverable (no matching pub)
                </li>
              </ul>
              {Array.isArray(resyncPreview.to_repair) && resyncPreview.to_repair.length > 0 ? (
                <details className="mt-2">
                  <summary className="cursor-pointer font-semibold">Show first {Math.min(10, resyncPreview.to_repair.length)}</summary>
                  <ul className="mt-1 ml-4 list-disc space-y-0.5">
                    {resyncPreview.to_repair.slice(0, 10).map((r) => (
                      <li key={r.user_id} className="break-all">
                        <span className="font-mono">{r.email}</span> — {r.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setResyncOpen(false)}
              disabled={resyncBusy}
              data-testid="admin-users-resync-cancel"
              className="h-11 rounded-sm border-zinc-300"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmResync}
              disabled={resyncBusy || !resyncPreview || (resyncPreview.to_repair_count || 0) === 0}
              data-testid="admin-users-resync-confirm"
              className="h-11 rounded-sm bg-amber-600 hover:bg-amber-700 text-white font-semibold tracking-tight active:scale-[0.98]"
            >
              {resyncBusy ? "Repairing…" : `Yes, repair ${resyncPreview?.to_repair_count || 0} player(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={purgeOpen}
        onOpenChange={(v) => {
          if (purgeBusy) return;
          setPurgeOpen(v);
          if (!v) {
            setPurgePreview(null);
            setPurgeScope("");
          }
        }}
      >
        <DialogContent
          className="bg-white rounded-sm border-zinc-200 sm:max-w-md"
          data-testid="admin-purge-unpaid-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Purge unpaid predictions?
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Clears every prediction submitted this season by players on the
              chosen pub who have <strong>not</strong> paid the league entry fee.
              Players who&apos;ve paid are always safe. Choose the pub, run the
              dry run, then confirm.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">
              Pub / client
            </label>
            <select
              data-testid="admin-purge-unpaid-scope"
              value={purgeScope}
              disabled={purgeBusy}
              onChange={(e) => {
                setPurgeScope(e.target.value);
                setPurgePreview(null);
                if (e.target.value) runPurgeDryRun(e.target.value);
              }}
              className="w-full h-11 rounded-sm border border-zinc-300 bg-white px-3 text-sm"
            >
              <option value="">— Select a pub —</option>
              {assignablePubs.map((p) => (
                <option key={p.id} value={p.slug}>
                  {p.name} · /{p.slug}
                </option>
              ))}
            </select>

            {purgeBusy && !purgePreview ? (
              <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 py-2">Scanning…</p>
            ) : purgePreview ? (
              <div className="rounded-sm border border-red-300 bg-red-50 p-3 text-xs text-red-900" data-testid="admin-purge-unpaid-preview">
                {(purgePreview.affected_users || []).length === 0 ? (
                  <p className="font-semibold">Nothing to purge — every player on this pub has paid or has no predictions.</p>
                ) : (
                  <>
                    <p className="font-semibold mb-1">Dry run — what will be deleted:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>
                        <span className="font-semibold">{purgePreview.predictions_deleted}</span> prediction row(s)
                      </li>
                      <li>
                        <span className="font-semibold">{purgePreview.affected_users.length}</span> player(s) will have their predictions cleared:
                      </li>
                    </ul>
                    <ul className="mt-1 ml-6 list-disc space-y-0.5 max-h-32 overflow-auto">
                      {purgePreview.affected_users.slice(0, 20).map((u) => (
                        <li key={u.user_id} className="font-mono break-all">{u.email}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ) : null}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPurgeOpen(false)}
              disabled={purgeBusy}
              data-testid="admin-purge-unpaid-cancel"
              className="h-11 rounded-sm border-zinc-300"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmPurge}
              disabled={purgeBusy || !purgePreview || (purgePreview.affected_users || []).length === 0}
              data-testid="admin-purge-unpaid-confirm"
              className="h-11 rounded-sm bg-red-600 hover:bg-red-700 text-white font-semibold tracking-tight active:scale-[0.98]"
            >
              {purgeBusy
                ? "Clearing…"
                : `Yes, clear ${(purgePreview?.affected_users || []).length} player(s)' predictions`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
